import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserGroupIcon,
  CheckIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { supabase } from '../../lib/supabase';
import { adminUpdateApplicationStatus } from '../../services/adminApplicationService';
import { ProgramClass } from '../../types';
import {
  getProgramClasses,
  deleteProgramClass,
  getBatchClassApplicationStats,
  ClassApplicationStats,
} from '../../services/programClassService';
import ClassForm from './ClassForm';

interface ApplicationWithStudent {
  id: string;
  application_number: string;
  student_name: string;
  status: 'registered' | 'waiting' | 'cancelled';
  waiting_number: number | null;
  created_at: string;
  cancelled_at: string | null;
  applicant_type: 'self' | 'child';
  class_id: string;
}

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCourse } = useCourses();
  const [programClasses, setProgramClasses] = useState<ProgramClass[]>([]);
  const [classStats, setClassStats] = useState<Record<string, ClassApplicationStats>>({});
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [applications, setApplications] = useState<ApplicationWithStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'registered' | 'waiting' | 'cancelled'>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClass, setEditingClass] = useState<ProgramClass | null>(null);

  const course = getCourse(id || '');

  useEffect(() => {
    if (id) {
      loadProgramClasses();
    }
  }, [id]);

  useEffect(() => {
    if (selectedClassId) {
      loadApplications();
    }
  }, [selectedClassId]);

  const loadProgramClasses = async () => {
    setLoading(true);
    try {
      const classes = await getProgramClasses(id!);
      setProgramClasses(classes);

      if (classes.length > 0) {
        // 첫 번째 반 선택
        setSelectedClassId(classes[0].id);

        // 모든 반의 신청 현황 로드
        const classIds = classes.map(c => c.id);
        const stats = await getBatchClassApplicationStats(classIds);
        setClassStats(stats);
      }
    } catch (error) {
      console.error('Error loading program classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    if (!selectedClassId) return;

    try {
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('class_id', selectedClassId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading applications:', error);
      } else {
        setApplications(data || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleStatusChange = async (
    applicationId: string,
    newStatus: 'registered' | 'waiting' | 'cancelled'
  ) => {
    if (processingId) return;

    const confirmed = window.confirm(
      `정말로 이 신청을 "${newStatus === 'registered' ? '접수완료' : newStatus === 'waiting' ? '대기중' : '취소'}" 상태로 변경하시겠습니까?`
    );

    if (!confirmed) return;

    setProcessingId(applicationId);

    try {
      const response = await adminUpdateApplicationStatus({
        application_id: applicationId,
        new_status: newStatus,
      });

      if (response.success) {
        alert(
          response.data?.promoted
            ? `상태가 변경되었습니다.\n대기자 ${response.data.promoted.student_name}님이 자동으로 등록되었습니다.`
            : '상태가 변경되었습니다.'
        );
        // 목록 새로고침
        await loadApplications();
        // 통계 새로고침
        if (selectedClassId) {
          const stats = await getBatchClassApplicationStats([selectedClassId]);
          setClassStats(prev => ({ ...prev, ...stats }));
        }
      } else {
        alert(`오류: ${response.error?.message || '상태 변경에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAddClass = () => {
    setEditingClass(null);
    setShowClassForm(true);
  };

  const handleEditClass = (programClass: ProgramClass) => {
    setEditingClass(programClass);
    setShowClassForm(true);
  };

  const handleDeleteClass = async (classId: string, className: string) => {
    const confirmed = window.confirm(
      `정말로 "${className}" 반을 삭제하시겠습니까?\n\n⚠️ 주의: 신청자가 있는 반은 삭제할 수 없습니다.`
    );

    if (!confirmed) return;

    try {
      await deleteProgramClass(classId);
      alert('반이 삭제되었습니다.');
      await loadProgramClasses();
    } catch (error: any) {
      console.error('Error deleting class:', error);
      alert(error.message || '반 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleClassFormSuccess = async () => {
    await loadProgramClasses();
  };

  if (!course) {
    return (
      <div className="p-8">
        <p className="text-sm text-gray-600 mb-6">강의를 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate('/admin/courses')}
          className="inline-flex items-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-150"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          강의 목록으로 돌아가기
        </button>
      </div>
    );
  }

  const selectedClass = programClasses.find(c => c.id === selectedClassId);
  const selectedStats = selectedClassId ? classStats[selectedClassId] : null;

  // 필터링된 신청자 목록
  const filteredApplications = statusFilter === 'all'
    ? applications
    : applications.filter(app => app.status === statusFilter);

  // 반의 모집 상태를 계산
  const getClassRecruitmentStatus = (programClass: ProgramClass): 'recruiting' | 'upcoming' | 'completed' => {
    const now = new Date();
    const startTime = programClass.registration_start_time?.substring(0, 5) || '00:00';
    const endTime = programClass.registration_end_time?.substring(0, 5) || '23:59';
    const startDateTime = new Date(`${programClass.registration_start_date}T${startTime}:00+09:00`);
    const endDateTime = new Date(`${programClass.registration_end_date}T${endTime}:00+09:00`);
    const courseEndDate = new Date(programClass.course_end_date);

    if (now > courseEndDate) return 'completed';
    if (now >= startDateTime && now <= endDateTime) return 'recruiting';
    if (now < startDateTime) return 'upcoming';
    return 'completed';
  };

  const getRecruitmentStatusBadge = (status: 'recruiting' | 'upcoming' | 'completed') => {
    switch (status) {
      case 'recruiting':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">모집중</span>;
      case 'upcoming':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-600 border border-amber-200">모집예정</span>;
      case 'completed':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-slate-50 text-slate-600 border border-slate-200">종료</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'registered':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-emerald-50 text-emerald-600 border border-emerald-200 whitespace-nowrap">접수완료</span>;
      case 'waiting':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">대기중</span>;
      case 'cancelled':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200 whitespace-nowrap">취소</span>;
      default:
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200 whitespace-nowrap">{status}</span>;
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* 헤더 */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => navigate('/admin/courses')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150"
              title="목록으로 돌아가기"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{course.courseName}</h1>
              <p className="text-sm text-gray-500 mt-1">강의 상세 및 반 관리</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/admin/courses/edit/${id}`)}
            className="flex items-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            강의 정보 수정
          </button>
        </div>
      </div>

      {/* 반 관리 섹션 */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">반 목록</h2>
            <button
              onClick={handleAddClass}
              className="flex items-center px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              반 추가
            </button>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">로딩 중...</div>
        ) : programClasses.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-600 mb-6">아직 등록된 반이 없습니다.</p>
            <button
              onClick={handleAddClass}
              className="inline-flex items-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              첫 번째 반 추가하기
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {programClasses.map((programClass) => {
              const stats = classStats[programClass.id];
              const isSelected = selectedClassId === programClass.id;
              const recruitmentStatus = getClassRecruitmentStatus(programClass);
              return (
                <div
                  key={programClass.id}
                  className={`border rounded-lg p-4 transition-all duration-150 ${
                    isSelected
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {programClass.class_name}
                        </h3>
                        {getRecruitmentStatusBadge(recruitmentStatus)}
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">정원:</span>
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-gray-900 font-bold whitespace-nowrap">{stats?.registered_count || 0}/{programClass.max_students}명</span>
                            {stats && stats.waiting_count > 0 && (
                              <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-xs font-medium whitespace-nowrap">
                                대기 {stats.waiting_count}명
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">모집기간:</span>
                          <span className="text-gray-900 text-sm">{programClass.registration_start_date} ~ {programClass.registration_end_date}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">교육기간:</span>
                          <span className="text-gray-900 text-sm">{programClass.course_start_date} ~ {programClass.course_end_date}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">수업시간:</span>
                          <span className="text-gray-900 text-sm">{programClass.course_days?.join(', ')} {programClass.course_start_time}~{programClass.course_end_time}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">수강료:</span>
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="text-gray-900 font-bold whitespace-nowrap">{programClass.tuition.toLocaleString()}원</span>
                            {programClass.material_fee > 0 && (
                              <span className="text-gray-700 text-sm whitespace-nowrap">+ 재료비 {programClass.material_fee.toLocaleString()}원</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">공개상태:</span>
                          <span className={`font-bold whitespace-nowrap ${programClass.is_published ? 'text-emerald-600' : 'text-red-600'}`}>
                            {programClass.is_published ? '✅ 공개중' : '❌ 비공개'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => setSelectedClassId(programClass.id)}
                        className={`flex items-center px-4 py-2 text-sm rounded-lg font-medium transition-all duration-150 whitespace-nowrap ${
                          isSelected
                            ? 'bg-indigo-600 text-white shadow-md hover:bg-indigo-700'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-indigo-500 hover:text-indigo-600'
                        }`}
                      >
                        <UserGroupIcon className="h-4 w-4 mr-1.5" />
                        신청자 보기
                      </button>
                      <button
                        onClick={() => handleEditClass(programClass)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-150 border border-gray-200"
                      >
                        <PencilIcon className="h-4 w-4 mr-1.5" />
                        수정
                      </button>
                      <button
                        onClick={() => handleDeleteClass(programClass.id, programClass.class_name)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 border border-gray-200"
                      >
                        <TrashIcon className="h-4 w-4 mr-1.5" />
                        삭제
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 신청자 목록 (반이 선택된 경우만 표시) */}
      {selectedClass && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              신청자 관리 - <span className="text-indigo-600">{selectedClass.class_name}</span>
            </h2>

            {/* 통계 */}
            {selectedStats && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
                  <div className="text-sm text-emerald-700 font-medium mb-1">접수완료</div>
                  <div className="text-2xl font-semibold text-emerald-700">
                    {selectedStats.registered_count}명
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <div className="text-sm text-amber-700 font-medium mb-1">대기중</div>
                  <div className="text-2xl font-semibold text-amber-700">
                    {selectedStats.waiting_count}명
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
                  <div className="text-sm text-slate-700 font-medium mb-1">취소</div>
                  <div className="text-2xl font-semibold text-slate-600">
                    {selectedStats.cancelled_count}명
                  </div>
                </div>
              </div>
            )}

            {/* 상태 필터 */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setStatusFilter('registered')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'registered'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                접수완료
              </button>
              <button
                onClick={() => setStatusFilter('waiting')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'waiting'
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                대기중
              </button>
              <button
                onClick={() => setStatusFilter('cancelled')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'cancelled'
                    ? 'bg-slate-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                취소
              </button>
            </div>
          </div>

          {/* 신청자 테이블 */}
          <div className="overflow-x-auto">
            {filteredApplications.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-gray-500">
                신청자가 없습니다.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      신청번호
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      신청자
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      신청유형
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      상태
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      대기순번
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      신청일시
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application) => (
                    <tr key={application.id} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                        {application.application_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium whitespace-nowrap">
                        {application.student_name}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {application.applicant_type === 'self' ? '본인' : '자녀'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(application.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {application.waiting_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {new Date(application.created_at).toLocaleString('ko-KR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-center gap-2">
                          {application.status === 'waiting' && (
                            <button
                              onClick={() => handleStatusChange(application.id, 'registered')}
                              disabled={processingId === application.id}
                              className="p-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors duration-150 disabled:text-gray-400 disabled:hover:bg-transparent border border-gray-200"
                              title="접수완료로 변경"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                          )}
                          {application.status !== 'cancelled' && (
                            <button
                              onClick={() => handleStatusChange(application.id, 'cancelled')}
                              disabled={processingId === application.id}
                              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-150 disabled:text-gray-400 disabled:hover:bg-transparent border border-gray-200"
                              title="취소"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ClassForm 모달 */}
      {showClassForm && (
        <ClassForm
          programId={id!}
          programName={course.courseName}
          classToEdit={editingClass}
          onClose={() => {
            setShowClassForm(false);
            setEditingClass(null);
          }}
          onSuccess={handleClassFormSuccess}
        />
      )}
    </div>
  );
};

export default CourseDetail;
